/* eslint-disable react-refresh/only-export-components -- co-located helpers/constants */
import React from 'react';
import {
  InputAdornment,
  type TextFieldProps,
} from '@mui/material';
import { AppTextField } from '../design-system';
import { Phone } from '@mui/icons-material';
import { isValidPhone as sharedIsValidPhone, PHONE_VALIDATION_MESSAGE } from '@cnapse/shared/phone.utils.js';

interface PhoneFieldProps {
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  autoComplete?: string;
  name?: string;
  inputRef?: TextFieldProps['inputRef'];
  sx?: TextFieldProps['sx'];
}

/**
 * Reusable Phone Number Input Component with consistent validation
 * 
 * Validation Rules:
 * - Accepts various formats: (123) 456-7890, 123-456-7890, 1234567890, +1-123-456-7890
 * - Minimum 10 digits (excluding formatting characters)
 * - Maximum 15 digits (for international numbers)
 * - Trims whitespace
 * - Nullable (can be empty)
 * 
 * @example
 * ```tsx
 * const [phone, setPhone] = useState<string | null>('');
 * const [phoneError, setPhoneError] = useState('');
 * 
 * const validatePhone = (value: string | null) => {
 *   if (!value && required) return 'Phone number is required';
 *   if (value && !isValidPhone(value)) return 'Invalid phone number format';
 *   return '';
 * };
 * 
 * <PhoneField
 *   value={phone}
 *   onChange={(value) => {
 *     setPhone(value);
 *     setPhoneError(validatePhone(value));
 *   }}
 *   error={!!phoneError}
 *   helperText={phoneError}
 * />
 * ```
 */
export const PhoneField: React.FC<PhoneFieldProps> = ({
  value,
  onChange,
  onBlur,
  label = 'Phone Number',
  placeholder,
  required = false,
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  autoComplete = 'tel',
  name = 'phone',
  inputRef,
  sx,
}) => {
  const [internalError, setInternalError] = React.useState<string>('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.trim();
    // If empty string, set to null for nullable schema compatibility
    const valueToSet = newValue === '' ? null : newValue;
    onChange(valueToSet);
    
    // Validate on change
    if (newValue && !isValidPhone(newValue)) {
      const digitsOnly = newValue.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        setInternalError(`Please enter at least 10 digits (${digitsOnly.length}/10)`);
      } else if (digitsOnly.length > 15) {
        setInternalError('Phone number cannot exceed 15 digits');
      } else {
        setInternalError('Invalid phone number format');
      }
    } else if (required && !newValue) {
      setInternalError('Phone number is required');
    } else {
      setInternalError('');
    }
  };

  const handleBlur = () => {
    // Validate on blur
    const trimmedValue = value?.trim() || null;
    if (required && !trimmedValue) {
      setInternalError('Phone number is required');
    } else if (trimmedValue && !isValidPhone(trimmedValue)) {
      const digitsOnly = trimmedValue.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        setInternalError(`Please enter at least 10 digits (${digitsOnly.length}/10)`);
      } else if (digitsOnly.length > 15) {
        setInternalError('Phone number cannot exceed 15 digits');
      } else {
        setInternalError('Invalid phone number format');
      }
    } else {
      setInternalError('');
    }
    
    if (onBlur) {
      onBlur();
    }
  };

  const displayError = error || !!internalError;
  const displayHelperText = internalError || helperText;

  return (
    <AppTextField
      name={name}
      value={value || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      inputRef={inputRef}
      label={label}
      type="tel"
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      error={displayError}
      helperText={displayHelperText}
      fullWidth={fullWidth}
      autoComplete={autoComplete}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Phone fontSize="small" sx={{ color: 'text.secondary' }} />
          </InputAdornment>
        ),
      }}
      sx={sx}
    />
  );
};

/**
 * Validates phone number format
 * @param phone - Phone number string to validate
 * @returns true if valid phone number format
 */
export const isValidPhone = sharedIsValidPhone;

/**
 * Validates phone number with error message
 * @param phone - Phone number string to validate
 * @param required - Whether phone is required
 * @returns Error message or empty string if valid
 */
export const validatePhone = (phone: string | null, required: boolean = false): string => {
  const trimmedPhone = phone?.trim() || null;
  
  if (!trimmedPhone) {
    return required ? 'Phone number is required' : '';
  }
  
  if (!isValidPhone(trimmedPhone)) {
    return PHONE_VALIDATION_MESSAGE;
  }
  
  return '';
};

/**
 * Formats phone number to US format (123) 456-7890
 * @param phone - Phone number string
 * @returns Formatted phone number or original if invalid
 */
export const formatPhoneUS = (phone: string | null): string => {
  if (!phone) return '';
  
  const digitsOnly = phone.replace(/\D/g, '');
  
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }
  
  if (digitsOnly.length === 11 && digitsOnly[0] === '1') {
    return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  }
  
  return phone; // Return original if doesn't match expected format
};

export default PhoneField;
